/**
 * Tailwind CSS implementation of the Tabs component's styled wrappers.
 * Renders the container `<div>` and tab `<Button>` with Seeds tab CSS classes
 * from tabs.css instead of styled-components. Used by TabsHybrid for consumers
 * that pass no styled-system props.
 */

import * as React from "react";
import { Button } from "@sproutsocial/seeds-react-button";
import type { TypeButtonProps } from "@sproutsocial/seeds-react-button";

// Local cn — copied from ButtonTailwind.tsx (NOT imported from seeds-react-utilities,
// which does not export it). Merges strings and { className: boolean } maps.
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

interface TabsContainerTailwindProps {
  /** Mapped to a layout class; never forwarded to the DOM. */
  fullWidth?: boolean;
  /** Consumed/stripped so they never land on the <div> (styled-components
   * silently dropped these non-DOM props; a plain <div> would not). */
  selectedId?: string;
  onSelect?: (id: string) => void;
  className?: string;
  children?: React.ReactNode;
  // The container forwards the remaining tablist props (role, aria-label,
  // data-qa-tabs, onKeyDown, …). They originate from TypeTabsProps (a `ul`-based
  // type) but render onto a `div`, so accept them loosely here.
  [key: string]: unknown;
}

export const TabsContainerTailwind = React.forwardRef<
  HTMLDivElement,
  TabsContainerTailwindProps
>(
  (
    {
      fullWidth,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      selectedId,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onSelect,
      className,
      children,
      ...rest
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        "seeds-tab-container",
        { "seeds-tab-container-fullwidth": !!fullWidth },
        className
      )}
      {...(rest as unknown as React.HTMLAttributes<HTMLDivElement>)}
    >
      {children}
    </div>
  )
);
TabsContainerTailwind.displayName = "TabsContainerTailwind";

interface TabButtonTailwindProps extends TypeButtonProps {
  // Custom (non-DOM) props mapped to classes — must not be forwarded to Button.
  isSelected?: boolean;
  fullWidth?: boolean;
}

export const TabButtonTailwind = ({
  isSelected,
  fullWidth,
  className,
  children,
  ...rest
}: TabButtonTailwindProps) => (
  <Button
    className={cn(
      "seeds-tab-button",
      {
        "seeds-tab-button-selected": !!isSelected,
        "seeds-tab-button-fullwidth": !!fullWidth,
      },
      className
    )}
    {...rest}
  >
    {children}
  </Button>
);
